import { JSX } from 'react'
import { Badge, Button } from '@dgshahr/ui-kit'
import Card from '@dgshahr/ui-kit/Card'
import { useNavigate } from 'react-router-dom'
import useAuthStore from '@/store/useAuthStore'
import { IGetCorrectionsResponseType } from '@/api/corrections/type'
import IconCheckCircleOutlineIcon from '@/components/common/icons/IconCheckCircleOutline'
import IconCloseSquareIcon from '@/components/common/icons/IconCloseSquare'
import IconPencilOutlineIcon from '@/components/common/icons/IconPencilOutline'
import { TBadgeColor, EStepStatus } from '@/types/register-status'

const stepLabels: Record<number, string> = {
  1: 'مشخصات',
  2: 'دسته‌بندی محصولات',
  3: 'آدرس',
  4: 'بارگذاری مدارک'
}

const stepDescriptions: Record<number, string> = {
  1: 'نام و نام خانوادگی، کدملی، ایمیل، نام فروشگاه و وبسایت',
  3: 'جزئیات آدرس فروشگاه و انبار',
  4: 'پروانه کسب و لوگو'
}

const stepRoutes: Record<number, string> = {
  1: '/merchant-register/info',
  2: '/merchant-register/category',
  3: '/merchant-register/address',
  4: '/merchant-register/document-upload'
}

const statusMapping: Record<
  EStepStatus,
  { text: string; color: TBadgeColor; icon: JSX.Element }
> = {
  [EStepStatus.PENDING]: {
    text: 'در انتظار تایید',
    color: 'success',
    icon: <IconCheckCircleOutlineIcon />
  },
  [EStepStatus.NEEDS_REVISION]: {
    text: 'نیاز به اصلاح',
    color: 'error',
    icon: <IconCloseSquareIcon />
  }
}
type Props = {
  data: IGetCorrectionsResponseType
}

const PendingStatus = ({ data }: Props) => {
  const navigate = useNavigate()
  const { clearAuth } = useAuthStore()

  const handleClick = () => {
    clearAuth()
    navigate('/')
  }

  return (
    <div className="w-[500px] flex flex-col gap-8 items-center justify-around px-6 py-5 border rounded-2xl bg-white">
      <div className="w-full py-4">
        <p className="text-gray-800 pb-2 font-h4-bold">
          <span>{data?.full_name}</span> -{' '}
          {data?.is_individual ? 'حقیقی' : 'حقوقی'}
        </p>
        <p className="font-p1-regular text-gray-600">
          تا چند روز آینده نتیجه بررسی مدارک شما اعلام خواهد شد.
        </p>
      </div>
      <Card
        className="w-full tablet:w-[450px] !p-0"
        color="white"
        size="medium"
      >
        <div className="p-4">
          <h1 className="font-h4-regular text-gray-700 mb-2">وضعیت شما</h1>
          {data?.steps
            ?.filter((step) => step.step_no <= 4)
            .map((step) => {
              const status =
                statusMapping[step.status as keyof typeof statusMapping]
              return (
                <div
                  key={step.step_no}
                  className={`flex flex-col items-center border-b p-4 last:border-none`}
                >
                  <div className="flex w-full justify-between">
                    <p className="text-right flex justify-center items-center text-[13px] font-medium">
                      {stepLabels[step.step_no]}
                    </p>
                    <div className="flex items-center gap-2">
                      <Badge
                        color={status?.color ?? 'primary'}
                        size="large"
                        type="twoTone"
                        value={status?.text ?? ''}
                        valueType="text"
                        icon={status?.icon}
                      />
                      {step.status === 'NEEDS_REVISION' && (
                        <Button
                          variant="text"
                          rightIcon={<IconPencilOutlineIcon />}
                          onClick={() => navigate(stepRoutes[step.step_no])}
                        />
                      )}
                    </div>
                  </div>
                  {stepDescriptions[step.step_no] && (
                    <p className="text-[13px] text-right w-full text-gray-500 mt-1 font-normal">
                      {stepDescriptions[step.step_no]}
                    </p>
                  )}
                </div>
              )
            })}
        </div>
      </Card>
      <Button isFullWidth color="error" variant="outline" onClick={handleClick}>
        خروج از حساب کاربری
      </Button>
    </div>
  )
}

export default PendingStatus
